import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import type RouterService from '@ember/routing/router-service'
import { service } from '@ember/service'
import { toStructuredQuery, toMatchableData, matchItem } from 'teacher-ui-client/utils/search'
import { assert } from '@ember/debug'
import type InteractivePreviewService from 'teacher-ui-client/services/interactive-preview'
import { FastPhonicsLessonPreview } from 'teacher-ui-client/interactive-preview/engines'
import { LessonsPage } from 'teacher-ui-client/components/page/lessons'
import { FastPhonicsNav } from 'teacher-ui-client/components/lessons/fast-phonics/nav'
import { LessonsSearch } from 'teacher-ui-client/components/lessons/search'
import { FastPhonicsPhaseFilters } from 'teacher-ui-client/components/lessons/fast-phonics/phase-filters'
import { FastPhonicsLessonData } from 'teacher-ui-client/components/lessons/fast-phonics/lesson-data'
import { LessonPreviewTile } from 'teacher-ui-client/components/tiles/lesson/preview'
import { NoResultsMessage } from 'teacher-ui-client/components/no-results-message'
import { t } from 'ember-intl'
import type { LessonResource } from 'teacher-ui-client/components/tiles/lesson/resource/type'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type FastPhonicsLessonsPreviewsRoute from 'teacher-ui-client/subscription-type/lessons/fast-phonics/previews/route'

interface Args {
  model: ModelFor<FastPhonicsLessonsPreviewsRoute>
}

interface LessonPreviewViewModel {
  id: string
  title: string
  coverUrl?: string
  lessonResources: LessonResource[]
  lessonData: LessonDataViewModel
  openPreview?: () => void
}

export interface LessonDataViewModel {
  activityNumber: number
  focusSounds?: string[]
  wordsRead?: string[]
  alternatives?: string[]
  wordCount?: number
  phase?: number
}

interface Signature {
  Args: Args
  Element: Element
}

export class FastPhonicsPreviews extends Component<Signature> {
  @service router!: RouterService

  @service interactivePreview!: InteractivePreviewService

  get lessonPreviewViewModels(): LessonPreviewViewModel[] {
    if (!this.args.model.loaderInstance?.isSuccessful) return []

    const dataModel = this.args.model.loaderInstance.value ?? { lessonPreviews: [], resourcesByLessonData: {} }
    let entries = dataModel.lessonPreviews

    const query = this.router.currentRoute?.queryParams.searchQuery
    if (typeof query === 'string' && query.length > 0) {
      const structuredQuery = toStructuredQuery(query)

      entries = entries.filter((entry) => {
        const { title = '' } = entry.fields
        const { focusSounds = [], wordsRead = [] } = entry.fields.lessonData?.fields ?? {}
        const matchableData = toMatchableData([title, focusSounds.join(' '), wordsRead.join(' ')])

        return matchItem(matchableData, structuredQuery)
      })
    }

    return entries
      .map((entry) => {
        const lessonPreview = entry.fields
        assert('lessonData must be resolved', entry.fields.lessonData)
        const lessonData = entry.fields.lessonData.fields
        return {
          id: entry.sys.id,
          title: lessonPreview.title,
          coverUrl: lessonPreview.cover?.fields.file?.url,
          openPreview: this.getOpenPreview(lessonData.activityNumber, lessonPreview.title),
          lessonResources:
            dataModel.resourcesByLessonData[entry.fields.lessonData.sys.id]
              ?.map((resource) => {
                return {
                  id: resource.sys.id,
                  title: resource.fields.title,
                  resourceType: resource.fields.resourceType,
                  fileUrl: resource.fields.file?.fields.file?.url,
                  videoUrl: resource.fields.videoUrl,
                }
              })
              .sort((a, b) => a.resourceType.localeCompare(b.resourceType)) ?? [],
          lessonData: {
            activityNumber: lessonData.activityNumber,
            focusSounds: lessonData.focusSounds,
            wordsRead: lessonData.wordsRead,
            alternatives: lessonData.alternatives,
            phase: lessonData.phase,
          },
        }
      })
      .sort((a, b) => a.lessonData.activityNumber - b.lessonData.activityNumber)
  }

  getOpenPreview(activityNumber: number, title: string) {
    return () => {
      const preview = new FastPhonicsLessonPreview(this, this.interactivePreview.curriculum, String(activityNumber))
      this.interactivePreview.openPreview(preview, { title })
    }
  }

  <template>
    <LessonsPage
      @title={{t "pages.product.lessons.title" product=(t "products.fast-phonics")}}
      @loaderInstance={{@model.loaderInstance}}
    >
      <:header>
        <FastPhonicsNav />
        <LessonsSearch @themeKey={{ThemeKeys.blue}} />
        <FastPhonicsPhaseFilters />
      </:header>
      <:results>
        {{#each this.lessonPreviewViewModels as |viewModel|}}
          <LessonPreviewTile
            class="w-80"
            data-test-lesson-preview-entry={{viewModel.lessonData.activityNumber}}
            @entryId={{viewModel.id}}
            @title={{viewModel.title}}
            @coverUrl={{viewModel.coverUrl}}
            @coverClass="h-20"
            @openPreview={{viewModel.openPreview}}
            @lessonResources={{viewModel.lessonResources}}
            ...attributes
          >
            <FastPhonicsLessonData @lessonData={{viewModel.lessonData}} />
          </LessonPreviewTile>
        {{else}}
          <NoResultsMessage data-test-empty />
        {{/each}}
      </:results>
    </LessonsPage>
  </template>
}

export default RouteTemplate(FastPhonicsPreviews)
